import { Card, CardContent } from '@design-system';
import { Figure, PaymentSplitBar, metricFigureClassName } from '../components/figures';
import { ScenarioSummary } from '../components/scenario-summary';
import { SelectedCoverageSummary } from '../components/selected-coverage-summary';
import { StepFrame } from '../components/step-frame';
import { WalkthroughExploreSection } from '../components/walkthrough-explore-section';
import { useCareWalkthrough } from '../hooks/use-care-walkthrough';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { approxMoney } from '../lib/format';
import type { ConsumerStepProps } from './step-props';

export function WalkthroughSummaryStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { summary, selectedQuote, outOfPocket, scenario } = useCareWalkthrough(clientId);
  if (!summary || !selectedQuote || !scenario) return null;

  return (
    <StepFrame
      step="walkthrough-summary"
      title="How does the whole event come together?"
      subtitle={<ScenarioSummary scenario={scenario} variant="sentence" />}
      onBack={() => back('coverage')}
      primary={{ label: 'Apply for this policy', onClick: () => goTo('application-start') }}
      next="start your application. We’ll prefill what we can."
    >
      <SelectedCoverageSummary quote={selectedQuote} />
      <Card className="py-4">
        <CardContent className="px-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-8">
            <div className="order-2 sm:order-1">
              <Figure
                label="Projected policy payout"
                value={approxMoney(summary.paidByInsurance)}
                tone="positive"
                className={metricFigureClassName}
              />
            </div>
            <div className="order-3 sm:order-2">
              <Figure
                label="Left for you to cover"
                value={approxMoney(outOfPocket)}
                hint="What insurance isn’t expected to pay. You can cover it from savings, income or family help."
                className={metricFigureClassName}
              />
            </div>
            <div className="order-1 col-span-2 sm:order-3 sm:col-span-1">
              <Figure
                label="Total care cost"
                value={approxMoney(summary.totalCareCost)}
                className={metricFigureClassName}
              />
            </div>
            <div className="order-4 col-span-2 sm:col-span-3">
              <PaymentSplitBar
                total={summary.totalCareCost}
                insurance={summary.paidByInsurance}
                label={`Insurance is projected to pay about ${Math.round((summary.paidByInsurance / Math.max(1, summary.totalCareCost)) * 100)}% of the care cost`}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <WalkthroughExploreSection clientId={clientId} scenario={scenario} />
    </StepFrame>
  );
}
